import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { Observable, Subject } from 'rxjs';
import { ItemCombobox } from '@models-global/model-combobox-item';
import { AuditClaimSearchResult } from '@module-claims/models/claims-http.interface';
import { ClaimsService } from '@module-claims/services/claims.service';
import { IAsyncData } from '@module-claims/store/claims.state';
import { getISODateString } from '@utils-global/date-time';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-audit-claim-search-modal',
  templateUrl: './audit-claim-search-modal.component.html',
  styleUrls: ['./audit-claim-search-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AuditClaimSearchModalComponent
  extends DialogContentBase
  implements OnInit, OnDestroy
{
  claimSearchState$: Observable<IAsyncData<AuditClaimSearchResult>>;
  form!: UntypedFormGroup;
  idQualifierOptions: ItemCombobox[] = [{ text: 'NPI', value: 'npi' }];

  private readonly _destroy$ = new Subject();

  constructor(
    private _dialogRef: DialogRef,
    private _fb: UntypedFormBuilder,
    private _claimsService: ClaimsService
  ) {
    super(_dialogRef);
    this.claimSearchState$ = this._claimsService.auditClaimSearch$;
  }

  ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

  ngOnInit(): void {
    this._claimsService.clearAuditClaimSearch();
    this.initForm();

    this.claimSearchState$
      .pipe(takeUntil(this._destroy$))
      .subscribe((state) => {
        if (state.pending) {
          this.form.disable();
        } else {
          this.form.enable();
        }
      });
  }

  submitSearch(): void {
    if (this.form.pristine || this.form.invalid) {
      return;
    }

    const {
      dateOfService,
      serviceProviderIdentifierQualifier,
      serviceProviderIdentifier,
      prescriptionServiceReferenceNumber,
      fillNumber,
      ndc
    } = this.form.value;

    this._claimsService.clearAuditClaimSearch();
    this._claimsService.searchAuditClaim({
      dateOfService: getISODateString(dateOfService as Date),
      serviceProviderIdentifierQualifier,
      serviceProviderIdentifier: (serviceProviderIdentifier as string).trim(),
      prescriptionServiceReferenceNumber: (
        prescriptionServiceReferenceNumber as string
      ).trim(),
      fillNumber: (fillNumber as string).trim(),
      ndc: (ndc as string).trim()
    });
  }

  private initForm(): void {
    this.form = this._fb.group({
      dateOfService: [null, [Validators.required]],
      serviceProviderIdentifierQualifier: ['npi', [Validators.required]],
      serviceProviderIdentifier: [
        null,
        [
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          Validators.minLength(10),
          Validators.maxLength(10)
        ]
      ],
      prescriptionServiceReferenceNumber: [
        null,
        [Validators.required, Validators.maxLength(100)]
      ],
      fillNumber: [
        null,
        [
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          Validators.maxLength(10)
        ]
      ],
      ndc: [
        null,
        [
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          Validators.minLength(10),
          Validators.maxLength(11)
        ]
      ]
    });
  }
}
